import { useParams, useLocation } from "wouter";
import {
  ArrowLeft,
  PlayCircle,
  Loader2,
  Film,
  Clock,
  Swords,
  Eye,
  GamepadIcon,
  RefreshCw
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { formatDistance } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { useAuth } from "@/hooks/use-auth";
import { EnhancedHeader } from "@/components/ui/enhanced-header";
import PullToRefresh from "react-simple-pull-to-refresh";
import { Badge } from "@/components/ui/badge";

export default function GameHistory() {
  const [_, setLocation] = useLocation();
  const { gameId } = useParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { data: gameRoom } = useQuery({
    queryKey: ["game-room", gameId],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/game-rooms/${gameId}`);
      if (!res.ok) throw new Error("Failed to fetch game room");
      return res.json();
    },
  });

  const { data: rounds, isLoading } = useQuery({
    queryKey: ["game-rounds", gameId],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/game-rounds/${gameId}`);
      if (!res.ok) throw new Error("Failed to fetch game rounds");
      return res.json();
    },
  });

  // Replay match mutation
  const replayMatchMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/game-rooms/${gameId}/replay`);
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "Failed to replay match");
      }
      return res.json();
    },
    onSuccess: async (data) => {
      // Invalidate relevant queries
      await queryClient.invalidateQueries({ queryKey: ["game-rooms"] });
      // Redirect to the new game
      setLocation(`/game/${data.id}`);
    },
    onError: (error: Error) => {
      console.error("Replay error:", error);
      // Check if this is a token error
      if (
        error.message?.includes("session") ||
        error.message?.includes("token")
      ) {
        toast({
          title: "Session Expired",
          description: "Your login session has expired. Please log in again.",
          variant: "destructive",
          action: (
            <ToastAction altText="Login" onClick={() => setLocation("/auth")}>
              Login
            </ToastAction>
          ),
        });
        // Redirect to login after a short delay
        setTimeout(() => setLocation("/auth"), 2000);
      } else {
        toast({
          title: "Error",
          description: error.message || "Failed to replay match",
          variant: "destructive",
        });
      }
    },
  });

  // Handle pull-to-refresh
  const handleRefresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["game-room", gameId] });
    await queryClient.invalidateQueries({ queryKey: ["game-rounds", gameId] });
    return Promise.resolve();
  };

  if (isLoading)
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh]">
        <div className="relative w-16 h-16">
          <Loader2 className="h-16 w-16 animate-spin text-primary/40" />
          <Film className="h-8 w-8 text-primary absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
        </div>
        <p className="mt-4 text-sm text-white/60">Loading match details...</p>
      </div>
    );

  if (!rounds || !Array.isArray(rounds))
    return (
      <div className="text-center py-12 theme-border border-dashed rounded-xl border max-w-md mx-auto mt-12">
        <Film className="h-12 w-12 mx-auto mb-4 text-white/30" />
        <p className="text-lg font-medium text-white/80">No rounds found</p>
        <p className="text-sm text-white/50 mt-1">
          This match may be corrupted or incomplete
        </p>
        <Button className="mt-4" onClick={() => setLocation("/games")}>
          Return to Profile
        </Button>
      </div>
    );

  // Create Play Again button component for the header right content
  const PlayAgainButton = () => (
    gameRoom &&
    user &&
    (user.id === gameRoom.player1 || user.id === gameRoom.player2) && (
      <Button
        variant="ghost"
        size="icon"
        className="rounded-full bg-black/80 hover:bg-black/60 text-white w-10 h-10"
        onClick={() => replayMatchMutation.mutate()}
        disabled={replayMatchMutation.isPending}
      >
        {replayMatchMutation.isPending ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <GamepadIcon className="h-5 w-5" />
        )}
      </Button>
    )
  );

  return (
    
      <div className="container max-w-4xl mx-auto px-4 pb-20">
        {/* Enhanced header */}
        <EnhancedHeader 
          title="Match History" 
          backPath="/games"
          accentColor="text-primary"
          rightContent={<PlayAgainButton />}
        />
<PullToRefresh
      onRefresh={handleRefresh}
      pullingContent={
        <div className="text-center p-2">
          <RefreshCw className="h-6 w-6 mx-auto animate-spin text-primary" />
          <p className="text-sm text-white/60 mt-1">Pull to refresh...</p>
        </div>
      }
      refreshingContent={
        <div className="text-center p-4">
          <RefreshCw className="h-6 w-6 mx-auto animate-spin text-primary" />
          <p className="text-sm text-white/60 mt-1">Refreshing...</p>
        </div>
      }
    >
        <div className="space-y-6 mt-4">
          {gameRoom && (
            <Card className="overflow-hidden backdrop-blur-sm border border-white/5 mb-8 bg-gradient-to-r from-black/60 to-black/40">
              <CardHeader className="px-6 py-5 border-b border-white/5 bg-black/30">
                <div className="flex items-center gap-2">
                  <Swords className="h-5 w-5 text-primary" />
                  <CardTitle className="text-lg font-medium">
                    Match Details: {gameRoom.name}
                  </CardTitle>
                  <div
                    className={`ml-2 text-xs py-0 h-5 inline-flex items-center px-2 rounded-full bg-black/40 border border-white/10
                      ${gameRoom.gameType === "ranked" ? "text-yellow-400" : "text-white/70"}`}
                  >
                    {gameRoom.gameType === "ranked" ? "Ranked" : "Casual"}
                  </div>
                </div>
              </CardHeader>

              <div className="p-6">
                <div className="flex flex-col md:flex-row justify-between">
                  <div className="mb-4 md:mb-0">
                    <p className="text-sm text-white/50 mb-1">
                      Played on{" "}
                      {new Date(gameRoom.createdAt).toLocaleDateString(undefined, {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </p>
                    <p className="text-sm text-white/50">
                      {gameRoom.createdAt
                        ? formatDistance(new Date(gameRoom.createdAt), new Date(), {
                            addSuffix: true,
                          })
                        : "N/A"}
                    </p>
                  </div>

                  <div className="flex items-center gap-8 bg-black/20 px-6 py-4 rounded-lg">
                    <div className="text-center flex-1">
                      <p className="font-semibold mb-1 truncate">
                        {gameRoom.player1Info?.username}
                      </p>
                      <p
                        className={`text-3xl font-bold ${gameRoom.winner === gameRoom.player1 ? "text-primary" : "text-white"}`}
                      >
                        {gameRoom.player1Score}
                      </p>
                      {gameRoom.winner === gameRoom.player1 && (
                        <div className="mt-2 px-2 py-0.5 inline-flex items-center rounded-full bg-primary/20 text-primary text-xs border border-primary/20">
                          Winner
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-black/40 text-white/70">
                      vs
                    </div>

                    <div className="text-center flex-1">
                      <p className="font-semibold mb-1 truncate">
                        {gameRoom.player2Info?.username}
                      </p>
                      <p
                        className={`text-3xl font-bold ${gameRoom.winner === gameRoom.player2 ? "text-primary" : "text-white"}`}
                      >
                        {gameRoom.player2Score}
                      </p>
                      {gameRoom.winner === gameRoom.player2 && (
                        <div className="mt-2 px-2 py-0.5 inline-flex items-center rounded-full bg-primary/20 text-primary text-xs border border-primary/20">
                          Winner
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          )}

          <div className="flex items-center space-x-2 mb-6">
            <Clock className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-bold text-white">Game Rounds</h2>
          </div>

          <div className="space-y-4">
            {rounds
              .filter((round: any) => !(
                (round.player1Score === 0 || round.player1Score === null || round.player1Score === undefined) && 
                (round.player2Score === 0 || round.player2Score === null || round.player2Score === undefined)
              ))
              .sort((roundA: any, roundB: any) => Number(roundA.id) - Number(roundB.id))
              .map((round: any, index: number) => (
              <Card
                key={round.id || index}
                className="overflow-hidden border border-white/10 transition-all duration-300
                  hover:border-primary/20 bg-[#14161F] hover:bg-[#16192A] p-0"
              >
                <div className="px-4 py-3 border-b border-white/10 bg-black/20">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 flex items-center justify-center rounded-full bg-primary/20 text-xs font-medium text-primary border border-primary/30">
                        {index + 1}
                      </div>
                      <h3 className="text-sm font-medium">
                        Round {index + 1}
                      </h3>
                    </div>
                    
                    {round.winner && (
                      <div className="flex items-center gap-1.5">
                        <Badge variant="outline" className={`bg-black/40 border-white/10 text-xs
                          ${round.winner === round.player1 ? "text-primary" : "text-primary"}`}
                        >
                          {round.winner === round.player1 
                            ? gameRoom?.player1Info?.username 
                            : gameRoom?.player2Info?.username} won
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex flex-col items-center relative">
                      <div className="text-sm font-medium mb-1">
                        {gameRoom?.player1Info?.username}
                      </div>
                      <div
                        className={`text-3xl font-bold ${round.winner === round.player1 ? "text-primary" : "text-white"}`}
                      >
                        {round.player1Score ?? 0}
                      </div>
                      {round.winner === round.player1 && (
                        <div className="absolute -right-2 -top-2">
                          <div className="h-4 w-4 rounded-full bg-primary flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3 text-black">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="px-4 py-1.5 rounded-full bg-black/50 text-xs font-medium border border-white/10 text-white/70">
                      vs
                    </div>

                    <div className="flex flex-col items-center relative">
                      <div className="text-sm font-medium mb-1">
                        {gameRoom?.player2Info?.username}
                      </div>
                      <div
                        className={`text-3xl font-bold ${round.winner === round.player2 ? "text-primary" : "text-white"}`}
                      >
                        {round.player2Score ?? 0}
                      </div>
                      {round.winner === round.player2 && (
                        <div className="absolute -right-2 -top-2">
                          <div className="h-4 w-4 rounded-full bg-primary flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3 text-black">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Only show replay button if the round has gameplay sequence data */}
                {Array.isArray(round.gameplaySequence) &&
                  round.gameplaySequence.length > 0 && (
                    <div className="px-4 py-3 border-t border-white/10 bg-black/20">
                      <div className="flex justify-end">
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() =>
                            setLocation(`/replay/${gameId}/${round.id}`)
                          }
                        >
                          <Film className="h-3.5 w-3.5 mr-1" />
                          Watch Replay
                        </Button>
                      </div>
                    </div>
                  )}
              </Card>
            ))}
          </div>
        </div>
        </PullToRefresh>

      </div>
  );
}
